import { useState } from 'react'
import type { Session } from '@supabase/supabase-js'
import type { Booking } from '../../lib/types'
import { format } from 'date-fns'
import { StatusBadge, SectionLabel, Button, Spinner } from '@perch/ui'
import { formatPrice, timeAgo, humanizeSnakeCase } from '../../lib/format'
import { checkPrice, archiveBooking, reactivateBooking, deleteBooking } from '../../lib/admin-api'
import { ModalDialog } from '../ui/ModalDialog'
import { LinearStatusBadge } from './StatusBadges'
import { BookingTimeline } from './BookingTimeline'
import { RecordRebookDialog } from './RecordRebookDialog'

export function BookingDetailDialog({ booking, onClose, onDeleted, onArchived, onReactivated, onRebooked, session }: {
  booking: Booking
  onClose: () => void
  onDeleted?: (bookingId: string) => void
  onArchived?: (bookingId: string) => void
  onReactivated?: (bookingId: string) => void
  onRebooked?: (bookingId: string) => void
  session: Session
}) {
  const segments = booking.segments || []
  const [checkingPrice, setCheckingPrice] = useState(false)
  const [checkResult, setCheckResult] = useState<{ type: 'cheaper' | 'not_cheaper' | 'no_availability' | 'error'; message: string } | null>(null)
  const [showRebook, setShowRebook] = useState(false)
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false)
  const [pnrConfirmText, setPnrConfirmText] = useState('')
  const [deleting, setDeleting] = useState(false)
  const [deleteError, setDeleteError] = useState<string | null>(null)
  const [archiving, setArchiving] = useState(false)
  const [archiveError, setArchiveError] = useState<string | null>(null)
  const [showArchiveConfirm, setShowArchiveConfirm] = useState(false)
  const [reactivating, setReactivating] = useState(false)
  const [reactivateError, setReactivateError] = useState<string | null>(null)

  const isArchived = !!booking.archived_at

  const hasValidFutureDates = segments.some(
    (seg) => new Date(seg.departure_datetime) > new Date()
  )

  // Keyboard/click-out handling lives in ModalDialog; nested confirms
  // use their own ModalDialog and the dialog stack ensures only the
  // topmost one responds to Escape.

  const closeArchiveConfirm = () => {
    setShowArchiveConfirm(false)
    setArchiveError(null)
  }
  const closeDeleteConfirm = () => {
    setShowDeleteConfirm(false)
    setPnrConfirmText('')
    setDeleteError(null)
  }

  const formatDuration = (dep: string, arr: string) => {
    const diff = new Date(arr).getTime() - new Date(dep).getTime()
    if (diff <= 0) return null
    const hours = Math.floor(diff / 3600000)
    const mins = Math.floor((diff % 3600000) / 60000)
    return `${hours}h ${mins}m`
  }

  const errorMessage = (err: unknown) => (err instanceof Error ? err.message : 'Request failed')

  const handleCheckPrice = async () => {
    setCheckingPrice(true)
    setCheckResult(null)
    try {
      const data = await checkPrice(session, booking.id)
      if (data.result === 'cheaper') {
        const savings = formatPrice(data.savings, booking.is_award_booking)
        setCheckResult({ type: 'cheaper', message: `Price drop found! Save ${savings}` })
      } else if (data.result === 'no_availability') {
        setCheckResult({ type: 'no_availability', message: data.message || 'No availability found' })
      } else if (data.result === 'error') {
        setCheckResult({ type: 'error', message: data.message || 'Search failed' })
      } else {
        const current = formatPrice(
          booking.is_award_booking ? data.current_points : data.current_price,
          booking.is_award_booking,
        )
        setCheckResult({ type: 'not_cheaper', message: `Current price: ${current} — no drop` })
      }
    } catch (err) {
      setCheckResult({ type: 'error', message: errorMessage(err) })
    } finally {
      setCheckingPrice(false)
    }
  }

  const handleArchiveBooking = async () => {
    setArchiving(true)
    setArchiveError(null)
    try {
      await archiveBooking(session, booking.id)
      onArchived?.(booking.id)
      onClose()
    } catch (err) {
      setArchiveError(errorMessage(err))
    } finally {
      setArchiving(false)
    }
  }

  const handleReactivateBooking = async () => {
    setReactivating(true)
    setReactivateError(null)
    try {
      await reactivateBooking(session, booking.id)
      onReactivated?.(booking.id)
      onClose()
    } catch (err) {
      setReactivateError(errorMessage(err))
    } finally {
      setReactivating(false)
    }
  }

  const handleDeleteBooking = async () => {
    setDeleting(true)
    setDeleteError(null)
    try {
      await deleteBooking(session, booking.id, pnrConfirmText)
      onDeleted?.(booking.id)
      onClose()
    } catch (err) {
      setDeleteError(errorMessage(err))
    } finally {
      setDeleting(false)
    }
  }

  const pnrMatches = pnrConfirmText.toUpperCase() === booking.pnr.toUpperCase()

  // Award vs cash fields come from different columns (latest_points_cost vs
  // latest_price — see booking_summary view). potential_savings is dual-unit
  // by is_award_booking, so formatPrice dispatches on the same flag.
  const latestPriceDisplay = formatPrice(
    booking.is_award_booking ? booking.latest_points_cost : booking.latest_price,
    booking.is_award_booking,
  )
  const lowestRawValue = booking.is_award_booking ? booking.lowest_points_seen : booking.lowest_price_seen
  const lowestRawDate = booking.is_award_booking ? booking.lowest_points_date : booking.lowest_price_date
  const lowestPriceDisplay = lowestRawValue != null
    ? formatPrice(lowestRawValue, booking.is_award_booking)
    : null
  const potentialSavingsDisplay = booking.potential_savings != null
    ? formatPrice(booking.potential_savings, booking.is_award_booking)
    : null

  return (
    <>
    <ModalDialog
      onClose={onClose}
      header={
        <>
          <span className="text-lg text-stone-900 dark:text-stone-100">{booking.airline_code}</span>
          <span className="font-mono text-sm text-stone-500">{booking.pnr}</span>
          <StatusBadge
            variant={isArchived ? 'warning' : booking.is_active ? 'watching' : 'stable'}
            dot
          >
            {isArchived ? 'Archived' : booking.is_active ? 'Active' : 'Inactive'}
          </StatusBadge>
        </>
      }
    >
      <div className="flex-1 overflow-y-auto p-6 space-y-6">
          <div>
            <SectionLabel className="mb-3">Booking Info</SectionLabel>
            <div className="bg-stone-50 dark:bg-stone-800/50 rounded-xl p-4 grid grid-cols-2 gap-4 text-sm">
              <div>
                <span className="text-xs text-stone-400 dark:text-stone-500">User</span>
                <p className="text-stone-900 dark:text-stone-100 font-medium">{booking.user_name || 'Unknown'}</p>
              </div>
              <div>
                <span className="text-xs text-stone-400 dark:text-stone-500">Booking Date</span>
                <p className="text-stone-900 dark:text-stone-100">{format(new Date(booking.booking_date), 'MMM d, yyyy')}</p>
              </div>
              <div>
                <span className="text-xs text-stone-400 dark:text-stone-500">Cabin Class</span>
                <p className="text-stone-900 dark:text-stone-100 capitalize">{humanizeSnakeCase(booking.cabin_class) || '—'}</p>
              </div>
              <div>
                <span className="text-xs text-stone-400 dark:text-stone-500">Passengers</span>
                <p className="text-stone-900 dark:text-stone-100">
                  {booking.passenger_count} {booking.passenger_count === 1 ? 'traveler' : 'travelers'}
                </p>
              </div>
              {booking.fare_basis_code && (
                <div>
                  <span className="text-xs text-stone-400 dark:text-stone-500">Fare Basis</span>
                  <p className="font-mono text-stone-900 dark:text-stone-100">{booking.fare_basis_code}</p>
                </div>
              )}
              <div>
                <span className="text-xs text-stone-400 dark:text-stone-500">Paid</span>
                <p className="text-stone-900 dark:text-stone-100 font-medium">
                  {booking.is_award_booking
                    ? `${booking.points_used?.toLocaleString()} pts`
                    : `${booking.currency} ${booking.total_price_paid.toLocaleString()}`
                  }
                </p>
              </div>
              <div>
                <span className="text-xs text-stone-400 dark:text-stone-500">Currency</span>
                <p className="text-stone-900 dark:text-stone-100">{booking.currency}</p>
              </div>
              {booking.is_award_booking && (
                <>
                  <div>
                    <span className="text-xs text-stone-400 dark:text-stone-500">Points Program</span>
                    <p className="text-stone-900 dark:text-stone-100">{booking.points_currency || '—'}</p>
                  </div>
                </>
              )}
            </div>
          </div>

          {segments.length > 0 && (
            <div>
              <SectionLabel className="mb-3">
                Itinerary ({segments.length} segment{segments.length !== 1 ? 's' : ''})
              </SectionLabel>
              <div className="space-y-2">
                {segments.map((seg, i) => {
                  const duration = formatDuration(seg.departure_datetime, seg.arrival_datetime)
                  return (
                    <div key={i} className="bg-stone-50 dark:bg-stone-800/50 rounded-xl p-4">
                      <div className="flex items-center justify-between mb-2">
                        <div className="flex items-center gap-2">
                          <span className="text-xs text-stone-400 dark:text-stone-500">Segment {i + 1}</span>
                          <span className="font-mono text-sm font-medium text-stone-700 dark:text-stone-300">
                            {seg.carrier_code} {seg.flight_number}
                          </span>
                        </div>
                        <div className="flex items-center gap-2">
                          {seg.cabin_code && (
                            <span className="text-xs font-mono bg-stone-200 dark:bg-stone-700 px-1.5 py-0.5 rounded">
                              {seg.cabin_code}
                            </span>
                          )}
                          {seg.booking_class && (
                            <span className="text-xs font-mono bg-stone-200 dark:bg-stone-700 px-1.5 py-0.5 rounded">
                              {seg.booking_class}
                            </span>
                          )}
                        </div>
                      </div>
                      <div className="flex items-center gap-3">
                        <div className="text-center">
                          <p className="text-lg font-mono text-stone-900 dark:text-stone-100">{seg.origin}</p>
                          <p className="text-xs text-stone-500">{format(new Date(seg.departure_datetime), 'MMM d, h:mm a')}</p>
                        </div>
                        <div className="flex-1 flex flex-col items-center">
                          <div className="w-full border-t border-stone-300 dark:border-stone-600 relative">
                            <svg className="w-4 h-4 text-stone-400 absolute right-0 -top-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14 5l7 7m0 0l-7 7m7-7H3" />
                            </svg>
                          </div>
                          {duration && <span className="text-xs text-stone-400 mt-1">{duration}</span>}
                        </div>
                        <div className="text-center">
                          <p className="text-lg font-mono text-stone-900 dark:text-stone-100">{seg.destination}</p>
                          <p className="text-xs text-stone-500">{format(new Date(seg.arrival_datetime), 'MMM d, h:mm a')}</p>
                        </div>
                      </div>
                    </div>
                  )
                })}
              </div>
            </div>
          )}

          <div>
            <SectionLabel className="mb-3">Price Tracking</SectionLabel>
            <div className="bg-stone-50 dark:bg-stone-800/50 rounded-xl p-4 grid grid-cols-2 gap-4 text-sm">
              <div>
                <span className="text-xs text-stone-400 dark:text-stone-500">Latest Price</span>
                <p className="text-stone-900 dark:text-stone-100 font-medium">
                  {latestPriceDisplay}
                </p>
              </div>
              <div>
                <span className="text-xs text-stone-400 dark:text-stone-500">Last Checked</span>
                <p className="text-stone-900 dark:text-stone-100">{timeAgo(booking.last_checked_at)}</p>
              </div>
              {lowestPriceDisplay != null && (
                <>
                  <div>
                    <span className="text-xs text-stone-400 dark:text-stone-500">Lowest Price Ever</span>
                    <p className="text-stone-900 dark:text-stone-100 font-medium">
                      {lowestPriceDisplay}
                    </p>
                  </div>
                  <div>
                    <span className="text-xs text-stone-400 dark:text-stone-500">Lowest Price Date</span>
                    <p className="text-stone-900 dark:text-stone-100">
                      {lowestRawDate ? format(new Date(lowestRawDate), 'MMM d, yyyy') : '—'}
                    </p>
                  </div>
                </>
              )}
              {booking.has_cheaper_option && potentialSavingsDisplay != null && (
                <div className="col-span-2">
                  <span className="text-xs text-stone-400 dark:text-stone-500">Potential Savings</span>
                  <p className="text-emerald-600 dark:text-emerald-400 font-semibold text-lg">
                    {potentialSavingsDisplay}
                  </p>
                </div>
              )}
              <div>
                <span className="text-xs text-stone-400 dark:text-stone-500">Total Checks</span>
                <p className="text-stone-900 dark:text-stone-100">{booking.total_checks_count ?? '—'}</p>
              </div>
              <div>
                <span className="text-xs text-stone-400 dark:text-stone-500">Cheaper Alerts</span>
                <p className="text-stone-900 dark:text-stone-100">{booking.cheaper_alerts_count ?? '—'}</p>
              </div>
              {booking.currently_available != null && (
                <div>
                  <span className="text-xs text-stone-400 dark:text-stone-500">Available</span>
                  <p className={booking.currently_available ? 'text-emerald-600' : 'text-red-500'}>
                    {booking.currently_available ? 'Yes' : 'No'}
                  </p>
                </div>
              )}
              {booking.latest_error_message && (
                <div className="col-span-2">
                  <span className="text-xs text-red-500">Latest Error</span>
                  <p className="text-red-600 dark:text-red-400 text-sm">{booking.latest_error_message}</p>
                </div>
              )}
              {booking.linear_issue && (
                <div className="col-span-2">
                  <span className="text-xs text-stone-400 dark:text-stone-500">Linear Issue</span>
                  <div className="mt-1">
                    <LinearStatusBadge issue={booking.linear_issue} />
                  </div>
                </div>
              )}
              {booking.is_active && !isArchived && (
                <div className="col-span-2 pt-2 border-t border-stone-200 dark:border-stone-700">
                  <div className="flex items-center gap-2">
                    <Button onClick={handleCheckPrice} disabled={checkingPrice} size="sm">
                      {checkingPrice ? (
                        <>
                          <Spinner className="mr-1.5" />
                          Checking...
                        </>
                      ) : 'Check Price'}
                    </Button>
                    {booking.has_cheaper_option && (
                      <Button
                        onClick={() => setShowRebook(true)}
                        size="sm"
                        variant="emerald"
                      >
                        Record Rebook
                      </Button>
                    )}
                  </div>
                  {checkResult && (
                    <div className={`mt-2 px-3 py-2 rounded-lg text-sm ${
                      checkResult.type === 'cheaper'
                        ? 'bg-emerald-50 dark:bg-emerald-900/30 text-emerald-700 dark:text-emerald-400'
                        : checkResult.type === 'error'
                          ? 'bg-red-50 dark:bg-red-900/30 text-red-700 dark:text-red-400'
                          : 'bg-stone-100 dark:bg-stone-800 text-stone-600 dark:text-stone-400'
                    }`}>
                      {checkResult.message}
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>

          <div>
            <SectionLabel className="mb-3">Timeline</SectionLabel>
            <BookingTimeline bookingId={booking.id} session={session} />
          </div>

          {booking.notes && (
            <div>
              <SectionLabel className="mb-2">Notes</SectionLabel>
              <p className="text-sm text-stone-700 dark:text-stone-300 bg-stone-50 dark:bg-stone-800/50 rounded-xl p-4">
                {booking.notes}
              </p>
            </div>
          )}

          <div className="text-xs text-stone-400 dark:text-stone-500 space-y-1 pt-4 border-t border-stone-200 dark:border-stone-800">
            <p>Booking ID: {booking.id}</p>
            <p>Created: {format(new Date(booking.created_at), 'MMM d, yyyy h:mm a')}</p>
            {booking.archived_at && (
              <p>Archived: {format(new Date(booking.archived_at), 'MMM d, yyyy h:mm a')}</p>
            )}
          </div>

          <div className="pt-4 border-t border-stone-200 dark:border-stone-800">
            {isArchived ? (
              <div className="space-y-3">
                {/* Reactivate requires at least one future-dated segment. */}
                {hasValidFutureDates && (
                  <div>
                    <Button
                      onClick={handleReactivateBooking}
                      disabled={reactivating}
                      size="sm"
                      variant="emerald"
                    >
                      {reactivating ? (
                        <>
                          <Spinner className="mr-1.5" />
                          Reactivating...
                        </>
                      ) : 'Start Watching'}
                    </Button>
                    {reactivateError && (
                      <p className="mt-1.5 text-sm text-red-600 dark:text-red-400">{reactivateError}</p>
                    )}
                  </div>
                )}
                <Button onClick={() => setShowDeleteConfirm(true)} variant="danger" size="sm">
                  Delete Permanently
                </Button>
              </div>
            ) : (
              <div>
                <Button onClick={() => setShowArchiveConfirm(true)} variant="ghost" size="sm">
                  Archive Booking
                </Button>
              </div>
            )}
          </div>
      </div>
    </ModalDialog>

    {showArchiveConfirm && (
      <ConfirmDialog
        tone="warning"
        title="Archive Booking"
        subtitle={`${booking.airline_code} · ${booking.pnr}`}
        message="This will archive the booking and stop price monitoring. You can reactivate or permanently delete it later."
        confirmLabel={archiving ? 'Archiving...' : 'Archive Booking'}
        confirmVariant="amber"
        confirmDisabled={archiving}
        error={archiveError}
        onCancel={closeArchiveConfirm}
        onConfirm={handleArchiveBooking}
      />
    )}

    {showDeleteConfirm && (
      <ConfirmDialog
        tone="danger"
        title="Delete Permanently"
        subtitle={`${booking.airline_code} · ${booking.pnr}`}
        message={
          <>
            This will permanently delete this booking and all related data. This action cannot be undone. Type the PNR{' '}
            <span className="font-mono font-semibold text-stone-900 dark:text-stone-100">{booking.pnr}</span> to confirm.
          </>
        }
        confirmLabel={deleting ? 'Deleting...' : 'Delete Permanently'}
        confirmVariant="danger-filled"
        confirmDisabled={!pnrMatches || deleting}
        error={deleteError}
        onCancel={closeDeleteConfirm}
        onConfirm={handleDeleteBooking}
      >
        <input
          type="text"
          value={pnrConfirmText}
          onChange={(e) => setPnrConfirmText(e.target.value.toUpperCase())}
          placeholder={booking.pnr}
          className="w-full px-3 py-2 text-sm font-mono bg-stone-50 dark:bg-stone-800 border border-stone-200 dark:border-stone-700 rounded-lg text-stone-900 dark:text-stone-100 placeholder-stone-300 dark:placeholder-stone-600 focus:outline-none focus:ring-2 focus:ring-red-500/50"
        />
      </ConfirmDialog>
    )}

    {showRebook && (
      <RecordRebookDialog
        booking={booking}
        session={session}
        onClose={() => setShowRebook(false)}
        onRebooked={(bookingId) => {
          setShowRebook(false)
          onRebooked?.(bookingId)
        }}
      />
    )}
    </>
  )
}

// --- ConfirmDialog ---
// Extracted so archive + delete (and any future confirm) share exactly
// the same chrome: title, subtitle, icon, message, optional child form
// content, error row, Cancel/Confirm buttons.

type ConfirmTone = 'warning' | 'danger'

function ConfirmDialog({
  tone,
  title,
  subtitle,
  message,
  confirmLabel,
  confirmVariant,
  confirmDisabled,
  error,
  onCancel,
  onConfirm,
  children,
}: {
  tone: ConfirmTone
  title: string
  subtitle?: string
  message: React.ReactNode
  confirmLabel: string
  confirmVariant: 'amber' | 'danger-filled'
  confirmDisabled?: boolean
  error?: string | null
  onCancel: () => void
  onConfirm: () => void
  children?: React.ReactNode
}) {
  const iconConfig = tone === 'warning'
    ? {
        wrap: 'bg-amber-100 dark:bg-amber-900/40',
        stroke: 'text-amber-600 dark:text-amber-400',
        path: 'M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4',
      }
    : {
        wrap: 'bg-red-100 dark:bg-red-900/40',
        stroke: 'text-red-600 dark:text-red-400',
        path: 'M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126zM12 15.75h.007v.008H12v-.008z',
      }

  return (
    <ModalDialog onClose={onCancel} maxWidth="md">
      <div className="p-6 space-y-4">
        <div className="flex items-center gap-3">
          <div className={`w-10 h-10 rounded-full ${iconConfig.wrap} flex items-center justify-center flex-shrink-0`}>
            <svg className={`w-5 h-5 ${iconConfig.stroke}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d={iconConfig.path} />
            </svg>
          </div>
          <div>
            <h3 className="text-base font-medium text-stone-900 dark:text-stone-100">{title}</h3>
            {subtitle && <p className="text-sm text-stone-500 dark:text-stone-400">{subtitle}</p>}
          </div>
        </div>
        <p className="text-sm text-stone-600 dark:text-stone-300">{message}</p>
        {children}
        {error && <p className="text-sm text-red-600 dark:text-red-400">{error}</p>}
        <div className="flex items-center justify-end gap-3 pt-2">
          <Button onClick={onCancel} disabled={confirmDisabled} variant="ghost" size="sm">
            Cancel
          </Button>
          <Button onClick={onConfirm} disabled={confirmDisabled} variant={confirmVariant} size="sm">
            {confirmLabel}
          </Button>
        </div>
      </div>
    </ModalDialog>
  )
}
